// Forms: pick the fields, order them, mark what's required, and share. Inside courageMFG anyone who can open the
// table fills it in; shared by link, anyone with the link can (a supplier, a visitor), without signing in.
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { Field, FieldType, PersonRow, PublicFormDef, TableData, Value, View, ViewConfig } from "./pApi";
import { pApi, publicForm } from "./pApi";
import { FieldInput } from "./Editors";
import { EDITABLE, TYPE_INFO } from "./format";
import { Mark } from "../ui";
import { useLoad } from "../hooks";

type FormField = { key: string; name: string; type: FieldType; description: string; options: Field["options"] };

/** The form itself, as people fill it in. */
export function FormFill({ title, description, fields, required, links, people, onSubmit, askName, thanks }: {
  title: string; description?: string; fields: FormField[]; required: string[]; links?: TableData["links"]; people?: PersonRow[];
  onSubmit: (values: Record<string, unknown>, name?: string) => Promise<string | void>; askName?: boolean; thanks?: string;
}) {
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [name, setName] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setErr(null);
    const missing = fields.find((f) => required.includes(f.key) && (values[f.key] === undefined || values[f.key] === null || values[f.key] === "" || (Array.isArray(values[f.key]) && !(values[f.key] as unknown[]).length)));
    if (missing) { setErr(`${missing.name} is required`); return; }
    setBusy(true);
    try { const msg = await onSubmit(values, name || undefined); setDone(msg || thanks || "Thanks. Your answers were sent."); setValues({}); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };
  if (done) return (
    <div className="pv-form pv-form-done">
      <h2>{done}</h2>
      <button className="btn" onClick={() => setDone(null)}>Fill it in again</button>
    </div>
  );
  return (
    <form className="pv-form" onSubmit={(e) => { e.preventDefault(); submit(); }}>
      <h2>{title}</h2>
      {description && <p className="muted" style={{ whiteSpace: "pre-wrap" }}>{description}</p>}
      {askName && <div className="field"><label htmlFor="pf-name">Your name</label><input id="pf-name" className="input" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" /></div>}
      {fields.map((f) => (
        <div key={f.key} className="field">
          <label htmlFor={`pf-${f.key}`}>{f.name}{required.includes(f.key) && <span className="pv-req" aria-label="required"> *</span>}</label>
          {f.description && <span className="hint">{f.description}</span>}
          <FieldInput id={`pf-${f.key}`} f={{ ...f, id: f.key, position: 0, primary: false }} value={values[f.key] as Value} links={links} people={people} onChange={(v) => setValues((s) => ({ ...s, [f.key]: v }))} />
        </div>
      ))}
      {err && <div className="msg error" role="alert">{err}</div>}
      <button className="btn primary" type="submit" disabled={busy}>{busy ? "Sending…" : "Submit"}</button>
    </form>
  );
}

/** Setting up a form view, with a live preview that really submits. */
export function FormView({ data, view, people, onConfig, onShared, onSubmitted }: { data: TableData; view: View; people: PersonRow[]; onConfig: (c: ViewConfig) => void; onShared: (v: View) => void; onSubmitted: () => void }) {
  const cfg = view.config;
  const can = data.table.canEdit;
  const editable = data.fields.filter((f) => EDITABLE(f));
  const keys = (cfg.fields ?? []).filter((k) => editable.some((f) => f.key === k));
  const shown = keys.map((k) => editable.find((f) => f.key === k)!).filter(Boolean);
  const off = editable.filter((f) => !keys.includes(f.key));
  const req = cfg.required ?? [];
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const set = (c: ViewConfig) => onConfig(c);
  const move = (i: number, d: number) => { const n = [...keys]; const j = i + d; if (j < 0 || j >= n.length) return; [n[i], n[j]] = [n[j], n[i]]; set({ fields: n }); };
  const inside = `${location.origin}/p/${data.table.id}/form/${view.id}`;
  const outside = view.publicToken ? `${location.origin}/f/${view.publicToken}` : null;
  const copy = (s: string) => { navigator.clipboard?.writeText(s).then(() => { setCopied(s); setTimeout(() => setCopied(null), 1500); }); };
  const share = async (pub: boolean) => { setBusy(true); try { onShared(await pApi.shareForm(view.id, pub)); } finally { setBusy(false); } };
  const publicBlocked = shown.filter((f) => f.type === "person" || f.type === "link");
  return (
    <div className="pv-formset">
      <aside className="pv-formcfg panel">
        <div className="pad">
          <div className="field"><label htmlFor="fv-t">Title</label><input id="fv-t" className="input" disabled={!can} defaultValue={cfg.title ?? view.name} onBlur={(e) => e.target.value !== (cfg.title ?? view.name) && set({ title: e.target.value })} /></div>
          <div className="field"><label htmlFor="fv-d">Description</label><textarea id="fv-d" className="textarea" rows={3} disabled={!can} defaultValue={cfg.description ?? ""} onBlur={(e) => e.target.value !== (cfg.description ?? "") && set({ description: e.target.value })} /></div>
          <h4 className="pv-sub-h">Questions</h4>
          <ol className="pv-qs">
            {shown.map((f, i) => (
              <li key={f.key}>
                <span className="pg-ti">{TYPE_INFO[f.type].icon}</span><span className="pv-qs-n">{f.name}</span>
                <label className="pv-qs-r"><input type="checkbox" disabled={!can} checked={req.includes(f.key)} onChange={(e) => set({ required: e.target.checked ? [...req, f.key] : req.filter((k) => k !== f.key) })} /> Required</label>
                {can && <><button className="icon-btn" aria-label="Move up" disabled={!i} onClick={() => move(i, -1)}>↑</button><button className="icon-btn" aria-label="Move down" disabled={i === shown.length - 1} onClick={() => move(i, 1)}>↓</button>
                  <button className="icon-btn" aria-label={`Remove ${f.name}`} onClick={() => set({ fields: keys.filter((k) => k !== f.key), required: req.filter((k) => k !== f.key) })}>×</button></>}
              </li>
            ))}
          </ol>
          {can && off.length > 0 && <select className="select" value="" aria-label="Add a question" onChange={(e) => e.target.value && set({ fields: [...keys, e.target.value] })}><option value="">+ Add a question…</option>{off.map((f) => <option key={f.key} value={f.key}>{f.name}</option>)}</select>}
          <div className="field" style={{ marginTop: 16 }}><label htmlFor="fv-th">After sending, say</label><input id="fv-th" className="input" disabled={!can} placeholder="Thanks. Your answers were sent." defaultValue={cfg.thanks ?? ""} onBlur={(e) => e.target.value !== (cfg.thanks ?? "") && set({ thanks: e.target.value })} /></div>

          <h4 className="pv-sub-h">Share</h4>
          <div className="pv-share-l"><span>Inside courageMFG</span><button className="linkish" onClick={() => copy(inside)}>{copied === inside ? "Copied" : "Copy link"}</button><Link to={`/p/${data.table.id}/form/${view.id}`}>Open</Link></div>
          {can && (
            <label className="pv-cb" style={{ marginTop: 8 }}><input type="checkbox" disabled={busy} checked={!!view.publicToken} onChange={(e) => share(e.target.checked)} /> Anyone with the link can fill it in, without signing in</label>
          )}
          {outside && <div className="pv-share-l"><span className="mono" style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{outside}</span><button className="linkish" onClick={() => copy(outside)}>{copied === outside ? "Copied" : "Copy"}</button></div>}
          {outside && publicBlocked.length > 0 && <p className="hint">{publicBlocked.map((f) => f.name).join(", ")} {publicBlocked.length === 1 ? "isn't" : "aren't"} shown on the public form: picking people or records would show who and what is in the table.</p>}
        </div>
      </aside>
      <div className="pv-formprev">
        <div className="pv-prev-tag">Preview: submitting adds a record</div>
        <FormFill title={cfg.title || view.name} description={cfg.description} fields={shown} required={req} links={data.links} people={people} thanks={cfg.thanks}
          onSubmit={async (values) => { await pApi.submitForm(view.id, values); onSubmitted(); }} />
      </div>
    </div>
  );
}

/** /p/:id/form/:viewId: a form inside courageMFG. */
export function FormPage() {
  const { id = "", viewId = "" } = useParams();
  const { data, error } = useLoad(() => Promise.all([pApi.table(id), pApi.people()]), [id]);
  if (error) return <div className="page"><div className="msg error">{(error as Error).message}</div></div>;
  if (!data) return <div className="page"><div className="empty">Loading…</div></div>;
  const [t, people] = data;
  const v = t.views.find((x) => x.id === viewId && x.type === "form");
  if (!v) return <div className="page"><div className="panel empty">That form doesn't exist any more. <Link to={`/p/${id}`}>Open {t.table.name}</Link></div></div>;
  const fields = (v.config.fields ?? []).map((k) => t.fields.find((f) => f.key === k)).filter((f): f is Field => !!f && EDITABLE(f));
  return (
    <div className="page pv-form-page">
      <nav className="crumbs"><Link to="/a/productivity">Productivity</Link><span>/</span><Link to={`/p/${id}`}>{t.table.name}</Link></nav>
      <FormFill title={v.config.title || v.name} description={v.config.description} fields={fields} required={v.config.required ?? []} links={t.links} people={people} thanks={v.config.thanks}
        onSubmit={async (values) => { await pApi.submitForm(v.id, values); }} />
    </div>
  );
}

/** /f/:token: a form shared by link. No sign-in, no app frame. */
export function PublicFormPage() {
  const { token = "" } = useParams();
  const [def, setDef] = useState<PublicFormDef | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { publicForm.get(token).then(setDef, (e) => setErr(e.message)); }, [token]);
  return (
    <div className="pv-public">
      <header><Mark size={22} /><span>courageMFG</span></header>
      {err ? <div className="pv-form"><h2>{err}</h2><p className="muted">Ask whoever sent you the link for a new one.</p></div>
        : !def ? <div className="empty">Loading…</div>
        : <FormFill title={def.title} description={def.description} fields={def.fields} required={def.required} askName
            onSubmit={async (values, name) => (await publicForm.submit(token, { values, name })).message} />}
      <footer className="muted">Sent to {def?.table ?? "a table"} in courageMFG</footer>
    </div>
  );
}
